'use client';

import { c as _c } from "react/compiler-runtime";
import type { OnrampClient } from '@morfi/onramp-sdk';
import { fundingKycScreen, isCustomerKycTerminal } from '@morfi/onramp-sdk';
import { type FundingCustomerVerification, useCustomerKyc } from '@morfi/onramp-sdk/react';
import type { MoonPayWebProvider } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { TOAST_IDS } from '@/config/toast-ids';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { FundingStatusHeader } from '../history/funding-status-header';
import { useFundingPageActive } from '../shared/use-funding-page-active';
import { FundingCustomerChallenge } from './funding-customer-challenge';
import { FundingHostedVerification } from './funding-hosted-verification';
import { FundingKycFields } from './funding-kyc-fields';
import { FundingPhoneKyc } from './funding-phone-kyc';
export function FundingCustomerVerificationForm(t0) {
  const $ = _c(91);
  const {
    client,
    provider,
    verification,
    onComplete,
    fromProfile: t1
  } = t0;
  const fromProfile = t1 === undefined ? false : t1;
  const form = useCustomerKyc(provider, verification, onComplete, useFundingPageActive());
  const t = useTranslations("onramp");
  let t2;
  if ($[0] !== form.busy || $[1] !== form.error || $[2] !== form.verification.status) {
    t2 = !form.busy && !isCustomerKycTerminal(form.verification.status, form.error) ? form.error : null;
    $[0] = form.busy;
    $[1] = form.error;
    $[2] = form.verification.status;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const transientError = t2;
  const announcedError = useRef(null);
  let t3;
  let t4;
  if ($[4] !== t || $[5] !== transientError) {
    t3 = () => {
      if (transientError && announcedError.current !== transientError) {
        showToast({
          id: TOAST_IDS.ONRAMP_VERIFICATION_ERROR,
          message: transientError === "access" ? t("kyc_access_error") : transientError === "invalid" ? t("kyc_invalid") : t("provider_unavailable"),
          variant: "error"
        });
      }
      announcedError.current = transientError;
    };
    t4 = [transientError, t];
    $[4] = t;
    $[5] = transientError;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[8] !== form.error) {
    t5 = () => {
      if (form.error) {
        logger.warn("onramp.customer_verification_failed", {
          category: form.error
        });
      }
    };
    t6 = [form.error];
    $[8] = form.error;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  if (isCustomerKycTerminal(form.verification.status, form.error)) {
    let t7;
    if ($[11] !== t) {
      t7 = t("kyc_terminal_title");
      $[11] = t;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== t) {
      t8 = t("kyc_terminal_description");
      $[13] = t;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== t7 || $[16] !== t8) {
      t9 = <FundingStatusHeader artwork="kyc-rejected" title={t7} description={t8} />;
      $[15] = t7;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  if (fundingKycScreen(form.verification) === "identity_complete" && !form.error) {
    let t7;
    if ($[18] !== t) {
      t7 = t("kyc_confirmed_title");
      $[18] = t;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    const t8 = fromProfile ? "profile_verified_description" : "kyc_confirmed_description";
    let t9;
    if ($[20] !== t || $[21] !== t8) {
      t9 = t(t8);
      $[20] = t;
      $[21] = t8;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== t7 || $[24] !== t9) {
      t10 = <FundingStatusHeader artwork="kyc-success" title={t7} description={t9} />;
      $[23] = t7;
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    const t11 = form.busy;
    const t12 = form.busy;
    const t13 = form.continuePayment;
    const t14 = fromProfile ? "profile_return" : "kyc_continue_payment";
    let t15;
    if ($[26] !== t || $[27] !== t14) {
      t15 = t(t14);
      $[26] = t;
      $[27] = t14;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    let t16;
    if ($[29] !== form.busy || $[30] !== form.continuePayment || $[31] !== t15) {
      t16 = <AnimatedButton loading={t11} disabled={t12} onClick={t13}>{t15}</AnimatedButton>;
      $[29] = form.busy;
      $[30] = form.continuePayment;
      $[31] = t15;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    let t17;
    if ($[33] !== t10 || $[34] !== t16) {
      t17 = <div className="flex flex-col gap-4">{t10}{t16}</div>;
      $[33] = t10;
      $[34] = t16;
      $[35] = t17;
    } else {
      t17 = $[35];
    }
    return t17;
  }
  const challenge = form.verification.challenge;
  if (fundingKycScreen(form.verification) === "provider_verification" && challenge) {
    let t7;
    if ($[36] !== challenge || $[37] !== form.refresh || $[38] !== provider) {
      t7 = <FundingCustomerChallenge provider={provider} challenge={challenge} onRefresh={form.refresh} />;
      $[36] = challenge;
      $[37] = form.refresh;
      $[38] = provider;
      $[39] = t7;
    } else {
      t7 = $[39];
    }
    return t7;
  }
  if (fundingKycScreen(form.verification) === "provider_verification") {
    let t7;
    if ($[40] !== form.refresh || $[41] !== provider) {
      t7 = <FundingHostedVerification provider={provider} onRefresh={form.refresh} />;
      $[40] = form.refresh;
      $[41] = provider;
      $[42] = t7;
    } else {
      t7 = $[42];
    }
    return t7;
  }
  if (fundingKycScreen(form.verification) === "phone_verification") {
    const t7 = `${client.cacheKey}:${form.verification.customerId}`;
    let t8;
    if ($[43] !== client || $[44] !== form.refresh || $[45] !== form.verification.customerId || $[46] !== provider || $[47] !== t7) {
      t8 = <FundingPhoneKyc key={t7} client={client} provider={provider} customerId={form.verification.customerId} onComplete={form.refresh} />;
      $[43] = client;
      $[44] = form.refresh;
      $[45] = form.verification.customerId;
      $[46] = provider;
      $[47] = t7;
      $[48] = t8;
    } else {
      t8 = $[48];
    }
    return t8;
  }
  const collecting = form.verification.status === "collecting" && form.fields.length > 0;
  if (!collecting && form.verification.status === "verifying" && !form.error) {
    let t7;
    if ($[49] !== t) {
      t7 = t("kyc_review_title");
      $[49] = t;
      $[50] = t7;
    } else {
      t7 = $[50];
    }
    let t8;
    if ($[51] !== t) {
      t8 = t("kyc_review_description");
      $[51] = t;
      $[52] = t8;
    } else {
      t8 = $[52];
    }
    let t9;
    if ($[53] !== t7 || $[54] !== t8) {
      t9 = <FundingStatusHeader artwork="kyc-pending" title={t7} description={t8} />;
      $[53] = t7;
      $[54] = t8;
      $[55] = t9;
    } else {
      t9 = $[55];
    }
    return t9;
  }
  let t7;
  if ($[56] !== form) {
    t7 = async () => {
      await form.submit();
    };
    $[56] = form;
    $[57] = t7;
  } else {
    t7 = $[57];
  }
  let t8;
  if ($[58] !== t) {
    t8 = t("kyc_title");
    $[58] = t;
    $[59] = t8;
  } else {
    t8 = $[59];
  }
  let t9;
  if ($[60] !== t8) {
    t9 = <h3 className="text-heading-md">{t8}</h3>;
    $[60] = t8;
    $[61] = t9;
  } else {
    t9 = $[61];
  }
  let t10;
  if ($[62] !== t) {
    t10 = t("kyc_help");
    $[62] = t;
    $[63] = t10;
  } else {
    t10 = $[63];
  }
  let t11;
  if ($[64] !== t10) {
    t11 = <p className="text-caption text-foreground-secondary">{t10}</p>;
    $[64] = t10;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  let t12;
  if ($[66] !== collecting || $[67] !== form || $[68] !== t) {
    t12 = collecting ? <FundingKycFields form={form} /> : <output className="text-caption">{form.verification.status === "verifying" ? t("kyc_verifying") : t("kyc_unavailable")}</output>;
    $[66] = collecting;
    $[67] = form;
    $[68] = t;
    $[69] = t12;
  } else {
    t12 = $[69];
  }
  let t13;
  if ($[70] !== collecting || $[71] !== form.unsupported || $[72] !== t) {
    t13 = form.unsupported && collecting && <p className="text-caption text-foreground-secondary">{t("kyc_additional_step")}</p>;
    $[70] = collecting;
    $[71] = form.unsupported;
    $[72] = t;
    $[73] = t13;
  } else {
    t13 = $[73];
  }
  let t14;
  if ($[74] !== collecting || $[75] !== form.busy || $[76] !== form.canSubmit || $[77] !== t) {
    t14 = collecting && <AnimatedButton type="submit" loading={form.busy} disabled={!form.canSubmit}>{form.busy ? t("checking") : t("verify_continue")}</AnimatedButton>;
    $[74] = collecting;
    $[75] = form.busy;
    $[76] = form.canSubmit;
    $[77] = t;
    $[78] = t14;
  } else {
    t14 = $[78];
  }
  let t15;
  if ($[79] !== collecting || $[80] !== form || $[81] !== t) {
    t15 = (!collecting || form.error) && <AnimatedButton type="button" variant="secondary" loading={form.busy} disabled={form.busy} onClick={() => {
      form.refresh();
    }}>{t("retry")}</AnimatedButton>;
    $[79] = collecting;
    $[80] = form;
    $[81] = t;
    $[82] = t15;
  } else {
    t15 = $[82];
  }
  let t16;
  if ($[83] !== t11 || $[84] !== t12 || $[85] !== t13 || $[86] !== t14 || $[87] !== t15 || $[88] !== t7 || $[89] !== t9) {
    t16 = <form className="rr-block flex flex-col gap-4" data-private="true" action={t7}>{t9}{t11}{t12}{t13}{t14}{t15}</form>;
    $[83] = t11;
    $[84] = t12;
    $[85] = t13;
    $[86] = t14;
    $[87] = t15;
    $[88] = t7;
    $[89] = t9;
    $[90] = t16;
  } else {
    t16 = $[90];
  }
  return t16;
}